/**
 * Segment options for filtering configurations
 */
export const SEGMENT_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'none', label: 'None' },
    { value: 'NEW_USERS', label: 'New Users' },
    { value: 'PLUS_MEMBERS', label: 'Plus Members' },
];

/**
 * Find slices in a target config that match SELECTED slices from the source config.
 * Matches by both original name AND displayType to prevent cross-type mismatches.
 * Uses original (saved) names to allow name changes to propagate.
 *
 * @param {Object} targetConfig - The config to search for matching slices
 * @param {Map<string, string>} selectedSliceInfo - Map of originalName -> displayType for selected slices
 * @returns {Array<{slice: Object, index: number}>} Array of matching slices with their indices
 */
export const getMatchingSlices = (targetConfig, selectedSliceInfo) => {
    if (!targetConfig?.slices) return [];

    return targetConfig.slices
        .map((slice, index) => ({ slice, index }))
        .filter(({ slice }) => {
            if (!slice.name) return false;
            const expectedType = selectedSliceInfo.get(slice.name);
            // Must match both name AND displayType
            return expectedType && slice.displayType === expectedType;
        });
};

/**
 * Apply converted source slice data to a matching target slice.
 * Preserves the target slice's id and carouselID (identity fields).
 * For product carousels (slices with carouselID), optionally preserves target's items if includeProducts is false.
 * For non-product slices (banners, etc.), always includes source items since they contain configuration data.
 *
 * @param {Object} targetSlice - The slice in the target config to update
 * @param {Object} convertedSourceSlice - The source slice with items converted for target's pack group
 * @param {boolean} includeProducts - Whether to copy products to target (only applies to product carousels)
 * @returns {Object} Updated slice with source data applied
 */
export const applySliceChanges = (targetSlice, convertedSourceSlice, includeProducts) => {
    // Only preserve identity fields from the target
    const { carouselID, id, items } = targetSlice;

    // Only apply includeProducts logic to product carousels (slices with carouselID)
    // For non-product slices (banners, etc.), items contain configuration data and should always be copied
    const isProductCarousel = !!carouselID;
    const shouldPreserveTargetItems = isProductCarousel && !includeProducts;

    return {
        ...convertedSourceSlice,
        id,
        carouselID,
        // Preserve target's items only for product carousels when not including products
        ...(shouldPreserveTargetItems ? { items } : {}),
    };
};

/**
 * Filter configs based on pack group and segment filters.
 *
 * @param {Array} configs - Array of config objects to filter
 * @param {string} filterPackGroup - Pack group filter value ('all' or pack group ID)
 * @param {string} filterSegment - Segment filter value ('all', 'none', or segment name)
 * @returns {Array} Filtered configs
 */
export const filterConfigs = (configs, filterPackGroup, filterSegment) =>
    configs.filter((config) => {
        // Pack group filter
        if (filterPackGroup !== 'all' && config.packGroupID !== parseInt(filterPackGroup, 10)) {
            return false;
        }
        // Segment filter
        if (filterSegment !== 'all') {
            if (filterSegment === 'none' && config.segment) {
                return false;
            }
            if (filterSegment !== 'none' && config.segment !== filterSegment) {
                return false;
            }
        }
        return true;
    });

/**
 * Get copyable slices from form data (slices with original names).
 *
 * @param {Array} slices - Array of slice objects from form data
 * @param {Map<string, string>} originalSliceNames - Map of slice ID -> original name
 * @returns {Array} Array of slices that can be copied
 */
export const getCopyableSlices = (slices, originalSliceNames) =>
    slices?.filter((slice) => {
        const originalName = originalSliceNames.get(slice.id);
        return originalName; // Only include slices with non-blank original names
    }) || [];

/**
 * @param {Array|null|undefined} currentSlices
 * @param {Map<string, { name: string, displayType: string }>} originalSliceIdentityById
 * @returns {Array<{ id: string, name: string, originalName: string, displayType: string, deleted: true }>}
 */
export const getDeletableSlices = (currentSlices, originalSliceIdentityById) => {
    const currentIds = new Set((currentSlices || []).map((s) => s.id));
    const deleted = [];

    originalSliceIdentityById.forEach((identity, id) => {
        if (!identity?.name || !identity?.displayType) return;
        if (currentIds.has(id)) return;
        deleted.push({
            id,
            name: identity.name,
            originalName: identity.name,
            displayType: identity.displayType,
            deleted: true,
        });
    });

    return deleted;
};

/**
 * @param {Array|null|undefined} currentSlices
 * @param {Map<string, { name: string, displayType: string }>} originalSliceIdentityById
 */
export const getApplicableSlices = (currentSlices, originalSliceIdentityById) => {
    const nameMap = new Map();
    originalSliceIdentityById.forEach((identity, id) => {
        if (identity?.name) nameMap.set(id, identity.name);
    });

    const copyable = getCopyableSlices(currentSlices, nameMap).map((slice) => ({
        ...slice,
        originalName: nameMap.get(slice.id),
        deleted: false,
    }));

    const deletable = getDeletableSlices(currentSlices, originalSliceIdentityById);

    return [...copyable, ...deletable];
};

/**
 * @param {Set<string>} selectedSliceIds
 * @param {Array<{ id: string, originalName?: string, displayType?: string }>} applicableSlices
 * @returns {Map<string, string>} originalName -> displayType
 */
export const buildSelectedSliceInfo = (selectedSliceIds, applicableSlices) => {
    const info = new Map();
    const byId = new Map((applicableSlices || []).map((s) => [s.id, s]));

    selectedSliceIds.forEach((sliceId) => {
        const row = byId.get(sliceId);
        if (row?.originalName && row?.displayType) {
            info.set(row.originalName, row.displayType);
        }
    });

    return info;
};

/**
 * @param {Array} slices
 * @param {Set<string>} keysToRemove - `${name}::${displayType}`
 */
export const removeSlicesMatchingKeys = (slices, keysToRemove) => {
    if (!slices?.length || !keysToRemove?.size) return slices || [];

    const remaining = slices.filter(
        (slice) => !slice?.name || !slice?.displayType || !keysToRemove.has(`${slice.name}::${slice.displayType}`),
    );

    return remaining;
};
